import { useState, useEffect } from 'preact/hooks';
import { store } from '../store';
import { leaveCheckoutHostPage, navigateToView } from '../navigation';
import { getConfig } from '../config';
import { getOrder } from '../api';
import { formatPrice } from '../utils/currency';
import { getOrderStatusBadge, getOrderStatusLabel } from '../utils/orderStatus';
import { t } from '../i18n';
import { TelegramPanel } from './TelegramPanel';

export function OrderSuccessPage() {
  const sorderid = store.getLastOrderId();
  const [order, setOrder] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(!!sorderid);

  useEffect(() => {
    if (!sorderid) return;
    const config = getConfig();
    getOrder(config, sorderid).then((res) => {
      setLoading(false);
      if (res.status === 200 && res.result && typeof res.result === 'object') {
        setOrder(res.result as Record<string, unknown>);
      }
    });
  }, [sorderid]);

  const products = order?.products && Array.isArray(order.products)
    ? order.products
    : order
      ? [{ product_title: order.product_title, productid: order.productid, amount: order.amount ?? 1, weight_value: order.weight_value, set_value: order.set_value }]
      : [];

  return (
    <div class="shop-widget-order-success py-4">
      <div class="card border-0 shadow-sm mx-auto sw-order-success-card">
        <div class="card-body text-center px-4 px-md-5 pt-4 pt-md-5 pb-3">
          <div class="sw-order-success-icon mx-auto mb-3" aria-hidden="true">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
              <path d="M8 12.5l2.5 2.5L16 9.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <h4 class="text-success fw-bold mb-2">{t('orderSuccess.placedSuccess')}</h4>
          <p class="text-muted mb-3">{t('orderSuccess.thanks')}</p>
          {sorderid && (
            <p class="mb-3">
              <span class="text-muted">{t('orderSuccess.orderId')}</span>{' '}
              <strong class="fs-5 text-body">#{sorderid}</strong>
            </p>
          )}
          <p class="text-muted small mb-0">{t('orderSuccess.description')}</p>
        </div>

        <div class="card-body border-top px-3 py-3">
          <TelegramPanel variant="order_info" />
        </div>

        {loading && (
          <div class="card-body border-top text-center py-3">
            <span class="spinner-border spinner-border-sm text-primary me-2" role="status" aria-hidden="true"></span>
            <span class="text-muted small">{t('orders.detailsLoading')}</span>
          </div>
        )}

        {order && !loading && (
          <div class="card-body border-top p-0">
            <div class="px-3 py-2 bg-light fw-bold small">{t('orderSuccess.orderDetails')}</div>
            <table class="table table-sm mb-0">
              <tbody>
                <tr>
                  <td class="fw-bold text-muted" style={{ width: '38%' }}>{t('orders.orderDate')}</td>
                  <td>{String(order.order_date ?? '-')}</td>
                </tr>
                <tr>
                  <td class="fw-bold text-muted">{t('orders.statusLabel')}</td>
                  <td><span class={`badge bg-${getOrderStatusBadge(order.status as string | undefined)}`}>{getOrderStatusLabel(order.status as string | undefined, t)}</span></td>
                </tr>
                <tr>
                  <td class="fw-bold text-muted">{t('orders.paymentMethod')}</td>
                  <td>{String(order.payment_method ?? '-')}</td>
                </tr>
                <tr>
                  <td class="fw-bold text-muted">{t('orders.shipping')}</td>
                  <td>{String(order.shipping_method ?? '-')}</td>
                </tr>
                <tr>
                  <td colSpan={2} class="bg-light small fw-bold pt-2">{t('orders.productsInfo')}</td>
                </tr>
                {products.map((p: any, idx: number) => (
                  <tr key={idx}>
                    <td colSpan={2}>
                      <span class="fw-bold">{p.product_title ?? order.product_title} #{p.productid ?? order.productid}</span>
                      {p.amount != null || order.amount != null ? ` × ${p.amount ?? order.amount ?? 1} ${t('orders.items')}` : ''}
                      {(p.weight_value || p.set_value || order.weight_value || order.set_value) ? ` · ${p.weight_value || order.weight_value || '-'} / ${p.set_value || order.set_value || '-'}` : ''}
                    </td>
                  </tr>
                ))}
                {order.subtotal != null && (
                  <tr>
                    <td class="fw-bold text-muted">{t('orders.subtotal')}</td>
                    <td>{formatPrice(order.subtotal as number)}</td>
                  </tr>
                )}
                {order.shipping_cost != null && (
                  <tr>
                    <td class="fw-bold text-muted">{t('orders.shippingCosts')}</td>
                    <td>{formatPrice(order.shipping_cost as number)}</td>
                  </tr>
                )}
                <tr class="table-success">
                  <td class="fw-bold">{t('orders.totalLabel')}</td>
                  <td class="fw-bold">{formatPrice(order.total as number | string | undefined)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div class="card-body border-top d-flex justify-content-center gap-2 flex-wrap pt-3 pb-4">
          <button type="button" class="btn btn-primary" onClick={() => navigateToView('orders')}>
            {t('orderSuccess.viewOrders')}
          </button>
          <button type="button" class="btn btn-outline-secondary" onClick={() => leaveCheckoutHostPage()}>
            {t('orderSuccess.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
